import { useContainer } from '@/plugins/inversify';
import { IConfigService, IConfigServiceId } from '@/services/IConfigService';
import { AppConfigDto } from '@/types/webapi';
import { useAppStore } from '@geta/kolumbus-frontend/features/common';
import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { reactive, toRefs, toValue } from 'vue';

export interface MainStore {
    ready: boolean;
    isLoading: boolean;
    isDebugMode: boolean | undefined;
    config: AppConfigDto & { supportedLanguages: string[] };
}

const defaultState = (): MainStore => ({
    ready: false,
    isLoading: false,
    isDebugMode: false,
    config: {
        kolumbusWebsiteBaseUrl: '',
        supportedLanguages: ['no', 'en'],
        googleMapsConfig: {
            apiKey: ''
        },
        silentLoginEnabled: true,
        defaultCulture: 'no',
        zoneMapPageUrl: '',
        postCodeServiceUrl: '',
        googleTagManagerConfig: {},
        googleRecaptcha: {
            siteKey: ''
        },
        azureMonitorConnectionString: '',
        azureMonitorClientSideTrackingEnabled: false
    }
});

const useMainStore = defineStore('mainStore', () => {
    const container = useContainer();
    const configService = container.get<IConfigService>(IConfigServiceId);
    const appStore = useAppStore();
    const state = reactive(defaultState());

    async function loadConfig() {
        try {
            // fetch app config
            const config = await configService.getConfig();

            // set up app state
            state.config = merge(state.config, config);

            return toValue(state.config);
        } catch (error) {
            registerError(error);

            return null;
        }
    }

    function reset(newState: MainStore) {
        Object.assign(state, newState);
    }

    function registerError(error: any) {
        appStore.registerError(error);
    }

    return {
        ...toRefs(state),
        loadConfig,
        reset,
        registerError
    };
});

export default useMainStore;
